import { useIntl } from 'react-intl';
import { DoiNotificationContent } from '@packages/cms-components/src/components/Forms/NewsletterSheet/DoiNotificationContent';
import { Box } from '@packages/shared/src/components/Box/Box';
import type { JSX } from 'react';
import { Sheet } from './Sheet';
import { subscribeSheetMessages } from './helpers/messages';
import { NewsletterSubscribeCompact } from './NewsletterSubscribeCompact';
import type { NewsletterSheetWrapperProps } from './types';

/**
 * Component for rendering the Newsletter Sheet.
 *
 * @param {NewsletterSheetWrapperProps} props - The properties object.
 * @param {NlSheetType | false} [props.nlTexts] - The newsletter sheet texts, if available.
 * @param {boolean} props.loggedIn - Indicates if the user is logged in.
 * @param {boolean} props.useDOI - Indicates if double opt-in is used.
 * @param {boolean} props.isNotificationOpen - Indicates if the notification is closed.
 * @param {Notification} props.notification - The notification object containing visibility and message.
 * @param {React.Dispatch<React.SetStateAction<Notification>>} props.setNotification - Function to set the notification state.
 * @param {React.Dispatch<React.SetStateAction<boolean>>} props.setForceHidden - Function to set the force hidden state.
 * @param {boolean} props.hasData - Indicates if the user has data.
 * @param {boolean} props.hasPermission - Indicates if the user has permission.
 * @param {boolean} props.isPending - Indicates if the request is pending.
 * @param {string} props.email - The email address of the user.
 * @returns {JSX.Element | null} The rendered component or null if conditions to hide are met.
 */
export const SheetWrapper = ({
  nlTexts,
  loggedIn,
  useDOI,
  isNotificationOpen,
  notification,
  setNotification,
  setForceHidden,
  hasData,
  hasPermission,
  isPending,
  email,
}: NewsletterSheetWrapperProps): JSX.Element | null => {
  const intl = useIntl();
  const isShowNotification =
    isNotificationOpen && isPending && email && useDOI && !notification.message;

  if (isShowNotification) {
    setNotification({
      message: (
        <DoiNotificationContent
          email={email}
          onClickedSuccess={() => setNotification({ ...notification, show: false })}
          onClickedError={(message) => setNotification({ message, show: true })}
        />
      ),
      show: true,
    });
  }

  const hideSheetLoggedIn = loggedIn && (!hasData || hasPermission || isPending);

  if (hideSheetLoggedIn) {
    return null;
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Sheet
        title={(nlTexts && nlTexts.label) || intl.formatMessage(subscribeSheetMessages.sheetTitle)}
        onClickClose={() => setForceHidden(true)}
      >
        <NewsletterSubscribeCompact />
      </Sheet>
    </Box>
  );
};
